import { AuthError, confirmUserAttribute } from "aws-amplify/auth";
import { AlertCircle } from "lucide-react";
import { useForm } from "react-hook-form";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { zodResolver } from "@hookform/resolvers/zod";

import {
  VerifyPhoneFormData,
  verifyPhoneSchema,
} from "../types/verify-phone.schema";

type VerifyPhoneFormProps = {
  onSuccess: () => Promise<void>;
};

export function VerifyPhoneForm({ onSuccess }: VerifyPhoneFormProps) {
  const form = useForm<VerifyPhoneFormData>({
    resolver: zodResolver(verifyPhoneSchema),
    defaultValues: {
      code: "",
    },
  });

  const handleSubmit = async (data: VerifyPhoneFormData) => {
    try {
      await confirmUserAttribute({
        userAttributeKey: "phone_number",
        confirmationCode: data.code,
      });
      await onSuccess();
    } catch (error) {
      if (error instanceof AuthError) {
        switch (error.name) {
          case "AuthValidationErrorCode":
          case "VerifyUserAttributeException":
          case "AuthTokenConfigException":
            form.setError("code", {
              type: "manual",
              message: "The code you entered is incorrect. Please try again.",
            });
            break;
          case "SetUserMFAPreferenceException":
            form.setError("root", {
              type: "manual",
              message: "Failed to set MFA preference. Please try again later.",
            });
            break;
          default:
            form.setError("root", {
              type: "manual",
              message: "The code you entered is incorrect. Please try again.",
            });
        }
      } else {
        console.error("Unexpected error:", error);
        form.setError("root", {
          type: "manual",
          message: "The code you entered is incorrect. Please try again.",
        });
      }
    }
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleSubmit)}
        id="verify-phone"
        className="space-y-6"
      >
        <FormField
          control={form.control}
          name="code"
          render={({ field }) => (
            <FormItem>
              <FormLabel isRequired>Enter Code</FormLabel>
              <FormControl>
                <InputOTP
                  maxLength={6}
                  {...field}
                  disabled={form.formState.isSubmitting}
                  onComplete={(code) => {
                    form.setValue("code", code);
                    form.handleSubmit(handleSubmit)();
                  }}
                  autoFocus
                  autoComplete="one-time-code"
                >
                  <InputOTPGroup>
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                    <InputOTPSlot index={3} />
                    <InputOTPSlot index={4} />
                    <InputOTPSlot index={5} />
                  </InputOTPGroup>
                </InputOTP>
              </FormControl>

              <FormMessage />
            </FormItem>
          )}
        />

        {form.formState.errors.root && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Unexpected error</AlertTitle>
            <AlertDescription>
              {form.formState.errors.root.message}
            </AlertDescription>
          </Alert>
        )}
      </form>
    </Form>
  );
}
